import { useMemo } from "react";
import { Plus, ChevronDown, LogOut, User, Settings, FileText, Users, PenTool, Megaphone, ChevronRight, Home, Moon, Sun } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { GlobalSearchCommand } from "./GlobalSearchCommand";
import { NotificationBell } from "./NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useTheme } from "@/components/theme-provider";
import { useWorkspace } from "@/hooks/useWorkspace";
import { supabase } from "@/integrations/supabase/client";

export function TopBar() {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const isClientWorkspace = currentWorkspace?.role === "client";
  const clientDetailId = useMemo(() => {
    const match = location.pathname.match(/^\/clientes\/([^/]+)$/);
    return match?.[1] ?? null;
  }, [location.pathname]);

  const { data: clientBreadcrumbLabel, isLoading: isClientBreadcrumbLoading } = useQuery({
    queryKey: ["topbar-breadcrumb-client", currentWorkspace?.id, clientDetailId],
    enabled: !!currentWorkspace?.id && !!clientDetailId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("name, company")
        .eq("workspace_id", currentWorkspace!.id)
        .eq("id", clientDetailId!)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data?.company || data?.name || null;
    },
  });

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const displayName = profile?.full_name || user?.user_metadata?.full_name || "Usuário";
  const userInitials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const routeLabels: Record<string, string> = {
    dashboard: isClientWorkspace ? "Visão geral" : "Dashboard",
    "minhas-tarefas": "Minhas Tarefas",
    clientes: "Clientes",
    demandas: "Demandas",
    calendario: "Calendário",
    conteudo: isClientWorkspace ? "Conteúdos" : "Conteúdo",
    aprovacoes: "Aprovações",
    ideias: "Banco de Ideias",
    arquivos: "Arquivos",
    ia: "IA Assistente",
    relatorios: isClientWorkspace ? "Relatórios" : "Métricas",
    timesheet: "Timesheet",
    financeiro: "Financeiro",
    crm: "Funil de Vendas",
    adherencia: "Régua de Aderência",
    trafego: "Tráfego Pago",
    campanhas: "Campanhas",
    comunicacao: "Comunicação",
    configuracoes: "Configurações",
    integracoes: "Integrações",
  };

  const isUuidLike = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  // Breadcrumbs Logic
  const getBreadcrumbs = () => {
    const path = location.pathname;
    if (path === "/dashboard") return [{ label: "Dashboard", path: "/dashboard" }];
    
    const parts = path.split("/").filter(Boolean);
    const breadcrumbs = [{ label: "Dashboard", path: "/dashboard" }];
    
    let currentPath = "";
    parts.forEach((part, index) => {
      currentPath += `/${part}`;
      const isLastPart = index === parts.length - 1;
      const label =
        clientDetailId && isLastPart && part === clientDetailId
          ? clientBreadcrumbLabel || (isClientBreadcrumbLoading ? "Carregando..." : "Cliente")
          : isUuidLike(part)
            ? "Detalhes"
            : routeLabels[part] || part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ");

      breadcrumbs.push({
        label,
        path: currentPath
      });
    });
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <header className="sticky top-0 z-20 flex h-16 max-w-full items-center justify-between gap-2 overflow-x-hidden border-b border-border/40 bg-background/80 px-4 backdrop-blur-md transition-all duration-200 md:gap-4 md:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-4">
        <SidebarTrigger className="text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-xl" />
        <Link to="/dashboard" className="hidden lg:flex items-center">
          <BrandLogo size="sm" />
        </Link>
        
        <div className="hidden md:flex items-center gap-1 text-sm text-muted-foreground">
          {breadcrumbs.map((crumb, index) => (
            <div key={crumb.path} className="flex items-center gap-1">
              {index > 0 && <ChevronRight className="h-4 w-4 opacity-50" />}
              <Link 
                to={crumb.path}
                className={`transition-colors flex items-center gap-1.5 ${
                  index === breadcrumbs.length - 1 ? "font-medium text-[#6B63CC]" : "hover:text-[#6B63CC]"
                }`}
              >
                {index === 0 && <Home className="h-3.5 w-3.5" />}
                {crumb.label}
              </Link>
            </div>
          ))}
        </div>

        <div className="md:hidden">
          <GlobalSearchCommand />
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 md:gap-3">
        <div className="hidden md:block">
          <GlobalSearchCommand />
        </div>

        {!isClientWorkspace && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="h-10 gap-1.5 rounded-xl border-0 px-3 text-white shadow-md shadow-[#4A42A8]/20 transition-all duration-200 md:gap-2 md:px-4">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline font-medium">Novo</span>
                <ChevronDown className="hidden h-4 w-4 opacity-70 sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/demandas?new=true")}>
                <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                Nova Demanda
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/clientes?new=true")}>
                <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                Novo Cliente
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/conteudo?new=true")}>
                <PenTool className="mr-2 h-4 w-4 text-muted-foreground" />
                Novo Conteúdo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/campanhas?new=true")}>
                <Megaphone className="mr-2 h-4 w-4 text-muted-foreground" />
                Nova Campanha
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <NotificationBell />

        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 rounded-full border border-border bg-card text-foreground hover:bg-muted hover:text-foreground"
          onClick={toggleTheme}
          aria-label={resolvedTheme === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
        >
          {resolvedTheme === "dark" ? <Sun className="h-4 w-4 text-[#C4A45A]" /> : <Moon className="h-4 w-4 text-[#4A42A8]" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.user_metadata?.avatar_url} />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="flex flex-col space-y-1 p-2">
              <p className="text-sm font-medium leading-none">
                {displayName}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user?.email}
              </p>
            </div>
            <DropdownMenuSeparator />
            {!isClientWorkspace && (
              <>
                <DropdownMenuItem onClick={() => navigate("/configuracoes?tab=profile")}>
                  <User className="mr-2 h-4 w-4" />
                  Meu Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/configuracoes")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Configurações
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
